import { ApiRequestError, type CommunityEntry, type SavedConfig } from "@cfg/api-contract";
import { buildConfiguratorSearch, FitPicker, getDraft, resolveShareUrl, saveDraft, scrollBehavior, useDesign, useFlash, useParams, type Draft, type FitPickerHandle, type FitTagValue } from "@cfg/configurator-ui";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useApp } from "../app-context.tsx";
import { useAuthUser } from "../auth.ts";
import { isAutoName, readableSummary, sameParams, type SummaryUi } from "../config-name.ts";
import { useFitSources } from "../fits.ts";
import { useCommunityCache } from "./CommunityPanel.tsx";
import { useMe } from "../me.ts";
import { DisplayNameDialog } from "./DisplayNameDialog.tsx";
import { RenameForm } from "./RenameForm.tsx";

export interface MyBracketProps {
  designId: string;
  version: string;
  /** Saved config (server) or draft (local) the configurator was opened from. */
  saved?: { kind: "config"; config: SavedConfig } | { kind: "draft"; draft: Draft };
  /** Name generated from the current params. */
  autoName: string;
  /** Name generated from other params (the ones last saved, for a rename back to the generated name). */
  autoNameFor: (params: Record<string, unknown>) => string;
  defaultsRef: React.MutableRefObject<Record<string, unknown>>;
  /** Listed community entries of this design's family, to say what these params are already tagged for. */
  communityItems: CommunityEntry[] | undefined;
  /** Open an entry in the community tab. */
  onShowEntry: (id: string) => void;
  /** Screen-reader announcement. */
  announce: (text: string) => void;
  /** "I printed it" from the Next steps card: open the tag step with the box ticked and the set filled in when known. A new `nonce` asks again. */
  tagRequest?: { nonce: number; fit?: { ref: string; name?: string } };
}

/** Refs of `fits`, as a short list ("42115-1, 10497-1"). */
const refsOf = (fits: { ref: string }[]) => fits.map((f) => f.ref).join(", ");

/**
 * "My bracket": the Medidas tab's block for the bracket on the model. Save and
 * Copy link keep it for yourself (profile or this device). Tagging is how a
 * bracket reaches the community: say what one set it fits, and the tag lands on
 * the community entry for exactly these dimensions, which nobody owns. Your
 * tag is yours; other people tag the same entry with theirs.
 */
export function MyBracket({ designId, version, saved, autoName, autoNameFor, defaultsRef, communityItems, onShowEntry, announce, tagRequest }: MyBracketProps) {
  const { brand, copy, api, auth, analytics } = useApp();
  const user = useAuthUser(auth);
  const me = useMe();
  const queryClient = useQueryClient();
  const cache = useCommunityCache(designId);
  const { description, spec } = useDesign();
  const { params } = useParams();
  const { flash, done } = useFlash();
  const features = brand.config.features;
  const fitConfig = brand.config.fitTags;
  const gallery = features.publicGallery && features.fitTags && !!api;
  const fitSources = useFitSources();

  const [saveState, setSaveState] = useState<{ busy: boolean; id?: string; kind?: "draft" | "config"; where: "profile" | "device" | null; error: string | null }>({
    busy: false,
    id: saved ? (saved.kind === "draft" ? saved.draft.id : saved.config.id) : undefined,
    kind: saved?.kind,
    where: null,
    error: null,
  });
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  /** The tag step: closed, open (set + printed + Tag), or done (the set, whether it became the user's tag, on which entry). */
  const [step, setStep] = useState<"closed" | "open" | { ref: string; added: boolean; id: string }>("closed");
  const [printed, setPrinted] = useState(false);
  const [tagging, setTagging] = useState<{ busy: boolean; error: string | null }>({ busy: false, error: null });
  /** A set waiting for the user to pick a display name (asked in a modal). */
  const [awaitingName, setAwaitingName] = useState<FitTagValue | null>(null);
  const picker = useRef<FitPickerHandle>(null);
  const printedBox = useRef<HTMLInputElement>(null);

  /** The name the user gave this bracket (saved or typed before saving); undefined = take it from `saved`. */
  const [nameOverride, setNameOverride] = useState<string | undefined>(undefined);
  const [renaming, setRenaming] = useState(false);
  const savedName = useMemo(() => {
    if (!saved || !description) return "";
    const s = saved.kind === "config" ? saved.config : saved.draft;
    return isAutoName(s.name, spec, s.params, description.defaults) ? "" : s.name;
  }, [saved, description, spec]);
  /** "" = named by its dimensions (the generated `autoName` is what gets stored). */
  const customName = nameOverride ?? savedName;
  const dims = description ? readableSummary(spec, params, description.defaults, description.ui as SummaryUi | undefined) : "";
  const storedName = customName || autoName;
  /** Params as last saved (the stored name describes these, not unsaved edits). */
  const savedParams = useRef<Record<string, unknown> | undefined>(saved ? (saved.kind === "config" ? saved.config.params : saved.draft.params) : undefined);

  /** Rename: kept for the next Save, and written at once when the bracket is already saved. */
  const rename = async (name: string) => {
    if (saveState.id && saveState.kind === "config" && user && api) {
      // Only the name: params changed since the last Save stay unsaved until Save.
      await api.updateConfig(saveState.id, { name: name || autoNameFor(savedParams.current ?? params) });
      void queryClient.invalidateQueries({ queryKey: ["configs"] });
    } else if (saveState.id && saveState.kind === "draft") {
      const d = await getDraft(saveState.id);
      if (d) await saveDraft({ ...d, name: name || autoNameFor(d.params) });
    }
    setNameOverride(name);
    setRenaming(false);
    announce(copy("configurator.renamed", { name: name || dims }));
  };

  // Changing the params makes earlier answers stale: the "saved" line, what was just tagged.
  useEffect(() => {
    setSaveState((s) => ({ ...s, where: null, error: null }));
    setStep((s) => (typeof s === "object" ? "closed" : s));
  }, [params]);

  /** The community entry for exactly these params, if anyone tagged them. */
  const listed = useMemo(
    () => (description ? communityItems?.find((i) => i.designId === designId && sameParams(i.params, params, description.defaults)) : undefined),
    [description, communityItems, designId, params],
  );

  /** Save to the profile (signed in) or this device. */
  const save = async () => {
    if (!description) return;
    setSaveState((s) => ({ ...s, busy: true, where: null, error: null }));
    try {
      if (user && api) {
        const cfg =
          saveState.kind === "config" && saveState.id
            ? await api.updateConfig(saveState.id, { name: storedName, params, version })
            : await api.createConfig({ name: storedName, designId, version, params });
        savedParams.current = params;
        setSaveState({ busy: false, id: cfg.id, kind: "config", where: "profile", error: null });
        window.history.replaceState(null, "", `${window.location.pathname}${buildConfiguratorSearch({ version, params, configId: cfg.id }, defaultsRef.current)}`);
        void queryClient.invalidateQueries({ queryKey: ["configs"] });
        finishSave(copy("configurator.savedProfile"));
        return;
      }
      const draft = await saveDraft({ id: saveState.kind === "draft" ? saveState.id : undefined, name: storedName, designId, version, params, brand: brand.config.id });
      setSaveState({ busy: false, id: draft.id, kind: "draft", where: "device", error: null });
      window.history.replaceState(null, "", `${window.location.pathname}${buildConfiguratorSearch({ version, params, configId: draft.id }, defaultsRef.current)}`);
      finishSave(copy("configurator.savedDevice"));
    } catch (err) {
      setSaveState((s) => ({ ...s, busy: false, error: (err as Error).message }));
    }
  };
  const finishSave = (text: string) => {
    flash("save");
    announce(text);
    analytics.track("save", designId, version);
  };

  /** Keep what is on the model as the start of a new bracket: the saved one stays as it is and the next Save creates another. */
  const startNew = () => {
    setSaveState({ busy: false, where: null, error: null });
    setNameOverride("");
    setShareUrl(null);
    window.history.replaceState(null, "", `${window.location.pathname}${buildConfiguratorSearch({ version, params }, defaultsRef.current)}`);
    announce(copy("configurator.startedNew"));
  };

  /** Open the tag step. Signed-out users sign in first. */
  const startTag = () => {
    if (!user) return void auth.signIn();
    setTagging({ busy: false, error: null });
    setStep("open");
  };

  // "I printed it" from the Next steps card.
  const tagButton = useRef<HTMLButtonElement>(null);
  /** A request waiting for the step to render. */
  const pendingRequest = useRef<MyBracketProps["tagRequest"]>(undefined);
  useEffect(() => {
    if (!tagRequest) return;
    if (!user) return void auth.signIn();
    pendingRequest.current = tagRequest;
    setTagging({ busy: false, error: null });
    setStep("open");
    setPrinted(true);
  }, [tagRequest?.nonce]);
  // Once the step is there: fill in the set and land on Tag (or on the set field when there is none).
  useEffect(() => {
    const req = pendingRequest.current;
    if (step !== "open" || !req) return;
    pendingRequest.current = undefined;
    if (req.fit) picker.current?.fill(req.fit);
    const target = req.fit ? tagButton.current : document.querySelector<HTMLInputElement>(".panel-publish-step .cfg-fit-ref");
    target?.scrollIntoView({ block: "center", behavior: scrollBehavior() });
    target?.focus({ preventScroll: true });
  }, [step, tagRequest?.nonce]);

  /** The step's Tag: the set in the field goes on the entry for these params. No display name yet: ask for one first. */
  const tag = () => {
    if (!api || !user || tagging.busy) return;
    const fit = picker.current?.take();
    if (!fit) return;
    // Known to have no display name: ask first (if the profile hasn't loaded, the API says so and we ask then).
    if (me.data && !me.data.displayName) return setAwaitingName(fit);
    void submitTag(fit);
  };
  const submitTag = async (fit: FitTagValue) => {
    if (!api) return;
    setTagging({ busy: true, error: null });
    try {
      const r = await api.tagDesign(designId, { version, params, fit, printed });
      cache.upsert(r.entry);
      cache.markMine(r.entry.id, r.added ? [fit.ref] : [], printed ? [fit.ref] : []);
      setStep({ ref: fit.ref, added: r.added, id: r.entry.id });
      setPrinted(false);
      setTagging({ busy: false, error: null });
      flash("tag");
      announce(r.added ? copy("community.tagged", { refs: fit.ref }) : copy("community.alreadyHasFit", { refs: fit.ref }));
    } catch (err) {
      const code = err instanceof ApiRequestError ? err.body?.code : undefined;
      setTagging({ busy: false, error: code === "display_name_required" ? null : (err as Error).message });
      if (code === "display_name_required") setAwaitingName(fit);
    }
  };

  /** Copies the link produced by *this* click (fresh /share/<token> for a saved config), never a stale one. */
  const share = async () => {
    const canToken = !!(api && user && saveState.kind === "config" && saveState.id);
    let url: string;
    try {
      ({ url } = await resolveShareUrl({
        origin: window.location.origin,
        currentHref: window.location.href,
        createToken: canToken ? async () => (await api!.shareConfig(saveState.id!)).token : undefined,
      }));
    } catch (err) {
      announce((err as Error).message);
      return;
    }
    analytics.track("share", designId, version);
    try {
      await navigator.clipboard.writeText(url);
      setShareUrl(null);
      flash("share");
      announce(copy("configurator.live.linkCopied", { url }));
    } catch {
      // clipboard may be unavailable: show the link to copy by hand
      setShareUrl(url);
      announce(copy("configurator.live.linkReady", { url }));
    }
  };

  const btnState = (id: string, busy?: boolean) => `${done === id ? " is-done" : ""}${busy ? " is-busy" : ""}`;

  return (
    <section className="panel-save" role="group" aria-labelledby="cfg-save-heading">
      <div className="panel-save-head">
        <h3 className="panel-save-title" id="cfg-save-heading">
          {copy("configurator.saveHeading")}
        </h3>
        {saveState.id && (
          <button type="button" className="cfg-text-btn panel-new" title={copy("configurator.newHint")} onClick={startNew}>
            + {copy("configurator.new")}
          </button>
        )}
      </div>
      {renaming ? (
        <RenameForm className="panel-rename" initial={customName} onSave={rename} onCancel={() => setRenaming(false)} />
      ) : (
        <div className="cfg-config-name-row">
          <p className="cfg-config-name" title={copy("mine.codeHint", { code: autoName })}>
            {customName || dims || autoName || "…"}
          </p>
          {customName && dims && <p className="cfg-config-dims muted small">{dims}</p>}
          <button type="button" className="link cfg-rename-btn" aria-label={copy("mine.renameLabel", { name: customName || dims || autoName })} onClick={() => setRenaming(true)} disabled={!description}>
            {copy("mine.rename")}
          </button>
        </div>
      )}

      <div className="panel-save-row">
        <button type="button" className={`cfg-btn cfg-save-btn${btnState("save", saveState.busy)}`} disabled={saveState.busy || !description} onClick={() => void save()}>
          {saveState.busy ? copy("configurator.saving") : done === "save" ? copy("configurator.done.save") : copy("configurator.save")}
        </button>
        {features.shareLinks && (
          <button type="button" className={`cfg-btn cfg-share-btn${btnState("share")}`} onClick={() => void share()} disabled={!description}>
            {done === "share" ? copy("configurator.done.share") : copy("configurator.share")}
          </button>
        )}
      </div>
      {saveState.where === "profile" && (
        <p className="muted small panel-save-status" role="status">
          {copy("configurator.savedProfile")} <a href="/mine">{copy("configurator.savedView")}</a>
        </p>
      )}
      {saveState.where === "device" && (
        <p className="muted small panel-save-status" role="status">
          {copy("configurator.savedDevice")}{" "}
          {auth.enabled && (
            <button type="button" className="link" onClick={() => void auth.signIn()}>
              {copy("auth.signIn")}
            </button>
          )}
        </p>
      )}
      {saveState.error && <p className="cfg-error">{saveState.error}</p>}
      {shareUrl && (
        <p className="muted small share-url">
          <a href={shareUrl}>{shareUrl}</a>
        </p>
      )}

      {gallery && (
        <div className="panel-publish">
          {listed && step !== "open" && typeof step !== "object" && (
            <p className="muted small panel-listed" role="status">
              {copy("community.alreadyTagged", { refs: refsOf(listed.fits) })}{" "}
              <button type="button" className="link" onClick={() => onShowEntry(listed.id)}>
                {copy("community.viewInCommunity")}
              </button>
            </p>
          )}
          {typeof step === "object" ? (
            <div className="panel-published" role="status">
              <p className="panel-published-status">
                ✓ {step.added ? copy("community.tagged", { refs: step.ref }) : copy("community.alreadyHasFit", { refs: step.ref })} ·{" "}
                <button type="button" className="link" onClick={() => onShowEntry(step.id)}>
                  {copy("community.viewInCommunity")}
                </button>
              </p>
              <button type="button" className="cfg-text-btn panel-tag-more" onClick={startTag}>
                + {copy("community.tagAnother", { label: fitConfig.label })}
              </button>
            </div>
          ) : step === "open" ? (
            <div className="panel-publish-step" role="group" aria-labelledby="cfg-publish-heading">
              <h4 className="panel-publish-title" id="cfg-publish-heading">
                {copy("community.tagTitle")}
              </h4>
              <p className="muted small">{copy("community.tagHint")}</p>
              <FitPicker
                ref={picker}
                label={copy("community.publishFitLabel", { label: fitConfig.label })}
                pattern={fitConfig.refPattern}
                refSuffix={fitConfig.refSuffix}
                suggestions={fitSources.suggestions}
                lookup={fitSources.lookup}
                hint={copy("fits.hint")}
                refPlaceholder={fitConfig.refPlaceholder}
                namePlaceholder={fitConfig.namePlaceholder}
                messages={{ invalid: copy("fits.invalid"), empty: copy("community.tagNeedsFit", { label: fitConfig.label }) }}
                // Enter in the set field goes on to "I printed it" rather than tagging past it; Tag submits.
                onSubmit={() => printedBox.current?.focus()}
              />
              <label className="cfg-check">
                <input ref={printedBox} type="checkbox" checked={printed} onChange={(e) => setPrinted(e.target.checked)} /> {copy("fits.printed")}
              </label>
              {tagging.error && <p className="cfg-error">{tagging.error}</p>}
              <div className="panel-save-row">
                <button ref={tagButton} type="button" className={`cfg-btn cfg-btn-primary cfg-publish-btn${btnState("tag", tagging.busy)}`} disabled={tagging.busy || !description} onClick={tag}>
                  {copy("community.tagSubmit")}
                </button>
                <button type="button" className="cfg-btn" onClick={() => setStep("closed")}>
                  {copy("community.cancel")}
                </button>
              </div>
            </div>
          ) : (
            <button type="button" className="cfg-btn cfg-publish-start" disabled={!description} onClick={startTag}>
              {copy("community.tag", { label: fitConfig.label })}
            </button>
          )}
        </div>
      )}
      <DisplayNameDialog
        open={!!awaitingName}
        onCancel={() => {
          // Nothing was tagged: say so, the set stays in the field for another try.
          setAwaitingName(null);
          setTagging({ busy: false, error: copy("community.tagNeedsName") });
        }}
        onDone={() => {
          const fit = awaitingName;
          setAwaitingName(null);
          if (fit) void submitTag(fit);
        }}
      />
    </section>
  );
}
